/**
 * CompanyDetailsTabs Component
 *
 * Renders the tab navigation bar for company details.
 * Handles tab selection, disabled states, sticky positioning
 * for similar companies tab, and optional tooltips.
 *
 * Each tab is rendered as a real <a href> so Cmd/Ctrl+Click,
 * middle-click, and native right-click "Open in new tab" work.
 * Plain left-click is intercepted for in-place tab switching
 * (no full page reload).
 */

import { useEffect, useState } from 'react';
import { Tooltip } from 'react-tooltip';
import { observer } from 'mobx-react';
import { DETAILS_POUP_TABS } from './constants/tabs';
import { useStore } from '../../../store/store';
import pubSub from '../../../utils/pubsub';
import { useContextMenuState } from '../../../pages/Home/components/profiler/shared/profilerNameSearchUtils';
import ContextMenuTooltip from '../../../pages/Home/components/profiler/shared/ContextMenuTooltip';
import { eventCompanyTabChanged } from '../../../ga4/ga4';

/**
 * @param {Object} props
 * @param {Array} props.tabs - Array of tab objects with id, slug, name, isActive, isDisabled, onClick, tooltipContent
 * @param {string} props.currentTab - Current active tab ID from DETAILS_POUP_TABS
 * @param {string} props.similarCompaniesTabId - ID of the similar companies tab for scroll behavior
 * @param {string} [props.companyId] - Company ID (required for href building)
 * @param {Object} [props.urlContext] - Query-param context to preserve across new-tab opens (ref, searchKey, searchId, comparablesId)
 * @param {boolean} [props.isStandalone=false] - True when rendered on the standalone /en/company-details/:id page; enables anchor behavior
 */
const CompanyDetailsTabs = observer(
  ({
    tabs,
    currentTab,
    similarCompaniesTabId,
    companyId,
    urlContext = {},
    isStandalone = false
  }) => {
    const { utilsStore } = useStore();
    const isStickyMode = currentTab === DETAILS_POUP_TABS.SIMILAR_COMPANIES;
    const [contextMenuTabId, setContextMenuTabId] = useState(null);
    const contextMenuState = useContextMenuState();

    // Drive the shared context-menu state from our per-tab selection. When the
    // shared hook auto-closes (outside click, or another context menu opening
    // via `icon-button-context-menu-trigger`), clear the selected tab too.
    useEffect(() => {
      if (!contextMenuState.toggledLinkContextMenu) {
        setContextMenuTabId(null);
      }
    }, [contextMenuState.toggledLinkContextMenu]);

    const buildTabHref = (tab) => {
      if (!isStandalone || tab.isDisabled || !tab.slug) return null;
      return utilsStore.buildCompanyDetailsUrl(companyId, { ...urlContext, tab: tab.slug });
    };

    const handleTabActivate = (tab) => {
      if (tab.isDisabled) return;

      if (!tab.isActive) {
        eventCompanyTabChanged(companyId, tab.slug || tab.id);
      }

      // Scroll to top when leaving similar companies tab (unless clicking it)
      if (tab.id !== similarCompaniesTabId && currentTab === DETAILS_POUP_TABS.SIMILAR_COMPANIES) {
        window.scrollTo({ top: 0 });
      }

      tab.onClick();
    };

    const handleAnchorClick = (e, tab) => {
      if (tab.isDisabled) {
        e.preventDefault();
        return;
      }
      // Let the browser handle modifier clicks, middle-clicks, and right-clicks natively
      if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) {
        return;
      }
      e.preventDefault();
      handleTabActivate(tab);
    };

    const handleContextMenu = (e, tab) => {
      if (tab.isDisabled || !isStandalone) return;
      // Suppress the native browser menu; show only our in-app menu
      // (matches the search-result cards pattern).
      e.preventDefault();
      pubSub.publish({ event: 'icon-button-context-menu-trigger' });
      setContextMenuTabId(tab.id);
      if (!contextMenuState.toggledLinkContextMenu) {
        contextMenuState.toggleLinkContextMenu();
      }
    };

    const openInNewTab = (tab) => {
      if (!isStandalone || tab.isDisabled || !tab.slug) return;
      utilsStore.openCompanyDetails(companyId, { ...urlContext, tab: tab.slug });
      contextMenuState.closeLinkContextMenu();
    };

    const handleTabKeyDown = (e, tab) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        handleTabActivate(tab);
      }
    };

    const renderTab = (tab) => {
      const className = `noselect tab${tab.isActive ? ' active' : ''}${
        tab.isDisabled ? ' disabled' : ''
      }`;

      if (isStandalone) {
        const href = buildTabHref(tab);
        return (
          <a
            key={tab.id}
            id={tab.id}
            className={className}
            href={href || undefined}
            aria-current={tab.isActive ? 'page' : undefined}
            aria-disabled={tab.isDisabled || undefined}
            tabIndex={tab.isDisabled ? -1 : 0}
            onClick={(e) => handleAnchorClick(e, tab)}
            onKeyDown={(e) => handleTabKeyDown(e, tab)}
            onContextMenu={(e) => handleContextMenu(e, tab)}
          >
            {tab.name}
          </a>
        );
      }

      // Fallback (overlay / modal mode): use a native <button> so keyboard
      // activation (Enter/Space) and screen readers work without extra ARIA.
      return (
        <button
          key={tab.id}
          id={tab.id}
          type="button"
          className={className}
          disabled={tab.isDisabled}
          onClick={() => handleTabActivate(tab)}
        >
          {tab.name}
        </button>
      );
    };

    const contextMenuTab = tabs.find((tab) => tab.id === contextMenuTabId && !tab.isDisabled);

    return (
      <>
        <div id="similar-tabs-scroll-starting-point" />
        <div className={`tabs-wrapper${isStickyMode ? ' sticky-similar-companies' : ''}`}>
          <div className="tabs-scrollable-wrap">
            <div className="tabs-wrap">{tabs.map((tab) => renderTab(tab))}</div>
          </div>
        </div>

        {tabs
          .filter((tab) => !!tab.tooltipContent)
          .map((tab) => (
            <Tooltip
              key={tab.id}
              anchorSelect={`#${tab.id}`}
              multiline={true}
              variant="dark"
              place="bottom-center"
              opacity={1}
              {...{ children: tab.tooltipContent }}
              clickable
              style={{ zIndex: 5 }}
            />
          ))}

        {isStandalone && contextMenuTab && (
          <ContextMenuTooltip
            anchorId={contextMenuTab.id}
            onOpenInNewTab={() => openInNewTab(contextMenuTab)}
          />
        )}
      </>
    );
  }
);

export default CompanyDetailsTabs;
